import React, { useState, useEffect } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import StatSelectionForm from '../QueryTool/StatSelectionForm';
import { getValueLabelFromFormInputs } from '../Common/CommonFunctions';
import LogOp from './LogOp';
import DropBlock from './DropBlock';
import DraggableFilter from './DraggableFilter';
import { columnManager } from '../Common/ColumnManager';
import { insertIntoBlock, removeFromTree, updateLogOpInTree, generateId, handleAfterDrop } from './filterFunctions';

function AdvancedFilters(props) {
    const [hidden, setHidden] = useState(true);
    const [message, setMessage] = useState('');
    const [newFilters, setNewFilters] = useState([]);
    const [stagingFilters, setStagingFilters] = useState([]);

    function toggleFilterDisplay() {
        setHidden(!hidden);
    }

    useEffect(() => {
        props.setHideFilters(hidden);
    }, [hidden]);

    function handleStagingDrop(filterNode, targetBlockId) {
        setStagingFilters(prev => insertIntoBlock(prev, targetBlockId, filterNode));
        handleAfterDrop();
    }

    function handleStagingRemove(nodeId) {
        setStagingFilters(prev => removeFromTree(prev, nodeId));
    }

    function handleStagingLogOpChange(nodeId, newOp) {
        setStagingFilters(prev => updateLogOpInTree(prev, nodeId, newOp));
    }

    function handleNewFiltersDrop(filterId) {
        setNewFilters(prev => {
            let draggableFilters = [];
            prev.forEach(prevFilter => {
                if (prevFilter.id !== filterId)
                    draggableFilters.push(prevFilter);
            })
            return draggableFilters;
        })
    }

    function applyToAll(section) {
        if (stagingFilters.length === 0) return;
        props.onApplyToAll && props.onApplyToAll(stagingFilters, section);
    }

    let sectionWidth = `-${document.getElementById('floating-filters')?.clientWidth}px`;

    return (
        <div style={{ left: hidden ? sectionWidth : '0px' }} id='floating-filters'>
            <FilterCreation
                selectableStatsByGroup={props.selectableStatsByGroup}
                colGroups={
                    props.selectableStatGroups.filter(group => {
                        return !['consecutive', 'throughX', 'dateRange'].includes(props.tabName)
                            || group.id !== 43;
                    })
                }
                tabName={props.tabName}
                timePeriodNotSelectable={true}
                hideFilters={true}
                onFilterCreated={(filterNode) => {
                    setNewFilters(prev => [...prev, filterNode]);
                }}
            />
            <Row>
                {newFilters.map(newFilter => {
                    return (
                        <DraggableFilter
                            key={newFilter.id}
                            filter={newFilter}
                            className={newFilter.filterType}
                            onRemove={() => handleNewFiltersDrop(newFilter.id)}
                        />
                    )
                }) }
            </Row>
            <Row style={{ minHeight: '36px' }}>
                <Col xs={3} xl={2}>
                    <Button variant='info' onClick={() => applyToAll('NumCrit')}>
                        Apply to All Numerical Criteria
                    </Button>
                </Col>
                <Col xs={3} xl={2}>
                    <Button variant='info' onClick={() => applyToAll('DisStat')}>
                        Apply to All Display Stats
                    </Button>
                </Col>
                <Col xs={9} lg={6} style={{ minHeight: '36px' }}>
                    {/* Staging area: renders all staging filter nodes */}
                    <div
                        className='dropZone dropZone-all'
                        onDragOver={(ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = 'move'; }}
                        onDrop={(ev) => {
                            ev.preventDefault();
                            const raw = ev.dataTransfer.getData('text/filter');
                            if (!raw) return;
                            try {
                                const filterNode = JSON.parse(raw);
                                handleStagingDrop(filterNode, null);
                            } catch (e) { console.error('Staging drop parse error', e); }
                        }}
                    >
                        {stagingFilters.map(node => {
                            if (node.kind === 'block') {
                                return (
                                    <DropBlock
                                        key={node.id}
                                        id={node.id}
                                        className={node.filterType}
                                        filterNodes={node.children}
                                        onDrop={handleStagingDrop}
                                        onRemove={handleStagingRemove}
                                        onLogOpChange={handleStagingLogOpChange}
                                        onRemoveBlock={() => handleStagingRemove(node.id)}
                                    />
                                );
                            }
                            if (node.kind === 'logOp') {
                                return (
                                    <LogOp
                                        key={node.id}
                                        id={node.id}
                                        operator={node.operator}
                                        onLogOpChange={(newOp) => handleStagingLogOpChange(node.id, newOp)}
                                    />
                                );
                            }
                            return null;
                        })}
                    </div>
                </Col>
            </Row>
            <Row className='justify-content-md-center'>
                {
                    message &&
                    <Col xs={6} style={{ border: 'solid 2px red', borderRadius: '5px', color: 'red', background: '#d2d2d2' }}>
                        {message}
                    </Col>
                }
            </Row>
            <span className='span-button' onClick={toggleFilterDisplay} type='button'>{hidden ? '>' : '<'}</span>
            <span className='span-filter-label'>ADVANCED FILTERS</span>
        </div>
    )
}

export default AdvancedFilters;

function FilterCreation(props) {
    const [iswFilter, setIswFilter] = useState({
        column: null,
        index: 1,
        stat: null,
        operator: '>',
        value: '0',
        timePeriod: props.timePeriodNotSelectable ? 'Career To Date' : 'Platform Year',
    });
    const [igwFilter, setIgwFilter] = useState({
        column: null,
        index: 1,
        stat: null,
        operator: '>',
        value: '0',
        timePeriod: props.timePeriodNotSelectable ? 'Career To Date' : 'Platform Year',
    });

    async function changeFilterByType(type, field, value) {
        let tempFilter = {};
        if (type === 'isw') tempFilter = Object.assign({}, iswFilter);
        else if (type === 'igw') tempFilter = Object.assign({}, igwFilter);

        tempFilter = await changeFilter(tempFilter, field, value);

        if (type === 'isw') setIswFilter(tempFilter);
        else if (type === 'igw') setIgwFilter(tempFilter);
    }

    async function changeFilter(tempFilter, field, value) {
        tempFilter[field] = value;

        if (field === 'stat') {
            let column = await columnManager.getSingleColumnById(tempFilter.stat);
            tempFilter.timePeriod =
                props.timePeriodNotSelectable
                    ? 'Career To Date'
                    : column.formInputs.defaultTimePeriod;

            tempFilter.column = column;
            switch (column.formInputs.inputType) {
                case 'Date':
                    tempFilter.value = new Date();
                    break;
                case 'DateNoYear':
                    tempFilter.operator = '=';
                    tempFilter.value = new Date();
                    break;
                case 'Select':
                    tempFilter.operator = '=';
                    tempFilter.value = column.formInputs.selectOptions[0]?.value ?? '0';
                    tempFilter.valueLabel = column.formInputs.selectOptions[0]?.label ?? '0';
                    break;
                case 'MultiSelect':
                    tempFilter.operator = '=';
                    tempFilter.value = null;
                    tempFilter.valueLabel = null;
                    break;
                case 'Number':
                    tempFilter.operator = '=';
                    tempFilter.value = 0;
                    tempFilter.valueLabel = null;
                    break;
                default:
                    tempFilter.operator = '=';
                    tempFilter.value = null;
                    tempFilter.valueLabel = null;
                    break;
            }
        }
        if (tempFilter.stat) {
            let column = await columnManager.getSingleColumnById(tempFilter.stat);
            tempFilter.valueLabel = getValueLabelFromFormInputs(
                column.formInputs,
                tempFilter.value
            )
        }
        return tempFilter
    }

    /**
     * Adds the filter to the list of draggable html elements.
     * @param {string} filterType Can either be "isw"(situational) or "igw"(in games where)
     */
    const createFilter = async (filterType) => {
        const inputFilter = filterType === 'isw' ? iswFilter : igwFilter;
        const filterTypeUpper = filterType === 'isw' ? 'ISW' : 'IGW';
        if (!inputFilter || !inputFilter.stat || inputFilter.stat === 'Select a Split') return;

        let filter = Object.assign({}, inputFilter);
        let column = await columnManager.getSingleColumnById(filter.stat);
        const splitTitle = props.selectableStatsByGroup.flat().find(c => c.key == filter.stat)?.title ?? String(filter.stat);
        const valueLabel = filter.valueLabel ?? filter.value;
        const filterText = `${splitTitle}${filter.operator}${valueLabel}`;

        const filterNode = {
            kind: 'filter',
            id: generateId(),
            statId: filter.stat,
            filterType: filterTypeUpper,
            operator: filter.operator,
            value: filter.value,
            granularity: column?.formInputs?.granularity,
            filterText,
        };

        props.onFilterCreated && props.onFilterCreated(filterNode);
    }

    return (
        <React.Fragment>
            <Row>
                <Col xs={9}>
                    <Row key='0'>
                        <Col className='d-none d-lg-block' lg={3}>
                            Split
                        </Col>
                        <Col className='d-none d-lg-block' lg={2}>
                            Operator
                        </Col>
                        <Col className='d-none d-lg-block' lg={2}>
                            Value
                        </Col>
                        <Col className='d-none d-lg-block' lg={1}></Col>
                    </Row>
                </Col>
                <Col xs={3} lg={2} />
            </Row>
            <Row>
                <Col xs={9}>
                    <StatSelectionForm
                        selectableStatsByGroup={props.selectableStatsByGroup.map(group => {
                            return group.filter(stat => stat.formInputs.isISWFilter);
                        })}
                        colGroups={props.colGroups.filter(group => group.groupId === 47)}
                        section={'Filter'}
                        tabName={props.tabName}
                        timePeriodNotSelectable={props.timePeriodNotSelectable}
                        hideFilters={props.hideFilters}
                        stats={[]}
                        curStat={iswFilter}
                        index={1}
                        setStat={async (index, field, value) => changeFilterByType('isw', field, value)}
                        placeholder='In Situations Where'
                    />
                </Col>
                <Col xs={3} lg={2}>
                    <Button variant='info' onClick={() => createFilter('isw')}>Create In Situations Where (ISW) Filter</Button>
                </Col>
            </Row>
            <Row>
                <Col xs={9}>
                    <StatSelectionForm
                        selectableStatsByGroup={props.selectableStatsByGroup.map(group => {
                            return group.filter(stat => stat.formInputs.isIGWFilter);
                        })}
                        colGroups={props.colGroups}
                        section={'Filter'}
                        tabName={props.tabName}
                        timePeriodNotSelectable={props.timePeriodNotSelectable}
                        hideFilters={props.hideFilters}
                        stats={[]}
                        curStat={igwFilter}
                        index={1}
                        setStat={async (index, field, value) => changeFilterByType('igw', field, value)}
                        placeholder='In Games Where'
                    />
                </Col>
                <Col xs={3} lg={2}>
                    <Button variant='info' onClick={() => createFilter('igw')}>Create In Games Where (IGW) Filter</Button>
                </Col>
            </Row>
        </React.Fragment>
    )
}